/**
 * VisitTypeNoteConfigEditor (demo-only)
 * ──────────────────────────────────────────────────────────────────────
 * Per-visit-type note configurator. Mirrored in the Scheduler settings
 * and under Lumina → Preferences. Backed by `visitTypeNoteConfigStore`
 * (localStorage). Callers MUST gate this component behind `isDemo`.
 *
 * Exposed controls:
 *   1. Narrative structure (Consult / SOAP / H&P) — pickers reset default
 *      section list when changed.
 *   2. Section structure: enable/disable, reorder (drag or arrow btns),
 *      rename label.
 *   3. Copy-forward: per-section on/off + per-section rule
 *      (always / same-visit / never).
 *   4. Assessment seeds: dynamic list of "A/P — …" blocks (Option B).
 *
 * Dirty-state model:
 *   Local draft state; user hits Save to persist. `onSaved` fires after
 *   localStorage write so parents can refetch / close panels.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import {
  ChevronUp, ChevronDown, GripVertical, Plus, Trash2, Save, RotateCcw, FileText,
} from 'lucide-react';
import {
  NARRATIVE_STRUCTURES, COPY_FORWARD_RULES,
  getVisitTypeNoteConfig, saveVisitTypeNoteConfig, resetVisitTypeNoteConfig,
  sectionsForStructure, addAssessmentSeed, removeAssessmentSeed, renameAssessmentSeed,
} from '../../../lib/demo/visitTypeNoteConfigStore';

const Toggle = ({ on, onToggle, testId, disabled }) => (
  <button
    onClick={onToggle}
    disabled={disabled}
    className={cn(
      'relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0',
      on ? 'bg-[#6b5ce7]' : 'bg-slate-200',
      disabled && 'opacity-50 cursor-not-allowed',
    )}
    style={{ border: 'none', cursor: disabled ? 'not-allowed' : 'pointer' }}
    data-testid={testId}
    role="switch"
    aria-checked={on}
    type="button"
  >
    <span className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform" style={{ transform: on ? 'translateX(18px)' : 'translateX(2px)' }} />
  </button>
);

const PanelCard = ({ title, desc, testId, children, right }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={testId}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-[12px] font-semibold text-slate-800">{title}</p>
        {desc && <p className="text-[10px] text-slate-500 mt-0.5">{desc}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

const VisitTypeNoteConfigEditor = ({ visitType, onSaved, surface = 'scheduler' }) => {
  const [draft, setDraft] = useState(() => getVisitTypeNoteConfig(visitType.id, 'consult'));
  const [dirty, setDirty] = useState(false);

  // Reload draft if the active visit type changes under us (e.g. user
  // switches rows in the parent list without unmounting the editor).
  useEffect(() => {
    setDraft(getVisitTypeNoteConfig(visitType.id, 'consult'));
    setDirty(false);
  }, [visitType.id]);

  const patchDraft = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setDirty(true);
  };

  const sortedSections = useMemo(
    () => [...(draft.sections || [])].sort((a, b) => a.order - b.order),
    [draft.sections],
  );

  /* ── Narrative structure ── */
  const handleStructureChange = (nextStructure) => {
    if (nextStructure === draft.narrativeStructure) return;
    // Reset sections to the new structure's defaults but preserve any
    // per-section copy-forward rules the user already set (merged by id).
    const nextSections = sectionsForStructure(nextStructure);
    const prevRules = draft.copyForward?.rules || {};
    const nextRules = {};
    const nextEnabled = {};
    for (const s of nextSections) {
      nextRules[s.id] = prevRules[s.id] || (s.id === 'chiefComplaint' ? 'never' : 'same-visit');
      nextEnabled[s.id] = s.enabled;
    }
    patchDraft({
      narrativeStructure: nextStructure,
      sections: nextSections,
      copyForward: { sectionsEnabled: nextEnabled, rules: nextRules },
    });
  };

  /* ── Sections: toggle / reorder / rename ── */
  const toggleSection = (id, on) => {
    const nextSections = (draft.sections || []).map((s) => (s.id === id ? { ...s, enabled: on } : s));
    const nextEnabled = { ...(draft.copyForward?.sectionsEnabled || {}), [id]: on };
    patchDraft({
      sections: nextSections,
      copyForward: { ...draft.copyForward, sectionsEnabled: nextEnabled },
    });
  };

  const moveSection = (id, delta) => {
    const ordered = [...sortedSections];
    const idx = ordered.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const j = idx + delta;
    if (j < 0 || j >= ordered.length) return;
    [ordered[idx], ordered[j]] = [ordered[j], ordered[idx]];
    const nextSections = ordered.map((s, i) => ({ ...s, order: i }));
    patchDraft({ sections: nextSections });
  };

  const renameSection = (id, label) => {
    const nextSections = (draft.sections || []).map((s) => (s.id === id ? { ...s, label } : s));
    patchDraft({ sections: nextSections });
  };

  /* ── Copy-forward rules ── */
  const setCopyRule = (sectionId, rule) => {
    const rules = { ...(draft.copyForward?.rules || {}), [sectionId]: rule };
    patchDraft({ copyForward: { ...draft.copyForward, rules } });
  };

  /* ── Assessment seeds (Option B: seed N, provider can add/remove live) ── */
  const addSeed = () => patchDraft({ assessments: { seeds: addAssessmentSeed(draft.assessments?.seeds || []) } });
  const removeSeed = (id) => patchDraft({ assessments: { seeds: removeAssessmentSeed(draft.assessments?.seeds || [], id) } });
  const renameSeed = (id, label) => patchDraft({ assessments: { seeds: renameAssessmentSeed(draft.assessments?.seeds || [], id, label) } });

  /* ── Save / Reset ── */
  const save = () => {
    saveVisitTypeNoteConfig(visitType.id, draft);
    setDirty(false);
    toast.success(`Note config saved for "${visitType.name}"`, { description: surface === 'preferences' ? 'Applied across Preferences and Scheduler.' : 'Applied across Scheduler and Preferences.' });
    if (onSaved) onSaved(draft);
  };

  const reset = () => {
    const next = resetVisitTypeNoteConfig(visitType.id, 'consult');
    setDraft(next);
    setDirty(false);
    toast.info('Reverted to the default Consult Report structure.');
  };

  return (
    <div className="space-y-4" data-testid="visit-type-note-config">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
            <FileText className="h-4 w-4 text-[#6b5ce7]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Note structure &amp; copy-forward</p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Controls how the Lumina note composer seeds and carries forward data for <b>{visitType.name}</b>.
              Changes apply across the Scheduler and Preferences.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-[11px] h-8 gap-1" onClick={reset} data-testid="vt-note-reset-btn">
            <RotateCcw className="h-3 w-3" /> Reset
          </Button>
          <Button size="sm" className="text-[11px] h-8 gap-1 bg-[#111] hover:bg-[#222]" onClick={save} disabled={!dirty} data-testid="vt-note-save-btn">
            <Save className="h-3 w-3" /> {dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>

      {/* 1 · Narrative structure */}
      <PanelCard
        title="Narrative structure"
        desc="Pick the default template for this visit type. The Consult Report is geared toward referral-driven visits (cardiology, specialty consults)."
        testId="vt-panel-narrative"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {NARRATIVE_STRUCTURES.map((opt) => {
            const selected = draft.narrativeStructure === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleStructureChange(opt.id)}
                data-testid={`vt-narrative-${opt.id}`}
                className={cn(
                  'text-left rounded-lg border p-3 transition-all',
                  selected ? 'border-[#6b5ce7] bg-violet-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300',
                )}
                style={{ cursor: 'pointer' }}
              >
                <div className="flex items-center justify-between">
                  <p className="text-[12px] font-semibold text-slate-800">{opt.label}</p>
                  {selected && <Badge className="text-[9px] bg-violet-100 text-violet-700 border-violet-200">Active</Badge>}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">{opt.desc}</p>
              </button>
            );
          })}
        </div>
      </PanelCard>

      {/* 2 · Section structure */}
      <PanelCard
        title="Sections — enable, reorder, rename"
        desc="Toggle sections on/off, move them up/down to change the note order, and rename the field label as it appears in the composer."
        testId="vt-panel-sections"
      >
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          {sortedSections.map((s, i) => (
            <div
              key={s.id}
              className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 bg-white"
              data-testid={`vt-section-row-${s.id}`}
            >
              <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" aria-hidden="true" />
              <div className="flex flex-col gap-0.5 shrink-0">
                <button
                  type="button"
                  onClick={() => moveSection(s.id, -1)}
                  disabled={i === 0}
                  className={cn('h-3.5 w-5 inline-flex items-center justify-center text-slate-500', i === 0 && 'opacity-30 cursor-not-allowed')}
                  style={{ background: 'none', border: 'none' }}
                  data-testid={`vt-section-up-${s.id}`}
                  aria-label={`Move ${s.label} up`}
                >
                  <ChevronUp className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => moveSection(s.id, +1)}
                  disabled={i === sortedSections.length - 1}
                  className={cn('h-3.5 w-5 inline-flex items-center justify-center text-slate-500', i === sortedSections.length - 1 && 'opacity-30 cursor-not-allowed')}
                  style={{ background: 'none', border: 'none' }}
                  data-testid={`vt-section-down-${s.id}`}
                  aria-label={`Move ${s.label} down`}
                >
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>
              <Input
                value={s.label}
                onChange={(e) => renameSection(s.id, e.target.value)}
                className="h-7 text-[12px] flex-1"
                data-testid={`vt-section-label-${s.id}`}
                aria-label={`Rename section ${s.label}`}
              />
              <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-500 tabular-nums">#{i + 1}</Badge>
              <Toggle
                on={!!s.enabled}
                onToggle={() => toggleSection(s.id, !s.enabled)}
                testId={`vt-section-toggle-${s.id}`}
              />
            </div>
          ))}
        </div>
      </PanelCard>

      {/* 3 · Copy-forward rules */}
      <PanelCard
        title="Copy-forward from prior note"
        desc="For each section, pick how data from the patient's last note should seed this encounter."
        testId="vt-panel-copy-forward"
      >
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-[9px] text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Section</th>
                <th className="text-left px-3 py-2 font-semibold">Copy-forward rule</th>
                <th className="text-right px-3 py-2 font-semibold">Enabled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {sortedSections.map((s) => {
                const rule = draft.copyForward?.rules?.[s.id] || 'same-visit';
                const enabled = !!draft.copyForward?.sectionsEnabled?.[s.id];
                return (
                  <tr key={s.id} data-testid={`vt-cf-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-700">{s.label}</td>
                    <td className="px-3 py-2">
                      <Select
                        value={rule}
                        onValueChange={(v) => setCopyRule(s.id, v)}
                      >
                        <SelectTrigger className="h-7 text-[11px] w-52" data-testid={`vt-cf-rule-${s.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {COPY_FORWARD_RULES.map((r) => (
                            <SelectItem key={r.id} value={r.id} className="text-[11px]">{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Toggle
                        on={enabled}
                        onToggle={() => {
                          const nextEnabled = !enabled;
                          const next = { ...(draft.copyForward?.sectionsEnabled || {}), [s.id]: nextEnabled };
                          patchDraft({ copyForward: { ...draft.copyForward, sectionsEnabled: next } });
                        }}
                        testId={`vt-cf-enabled-${s.id}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </PanelCard>

      {/* 4 · Assessment seeds */}
      <PanelCard
        title="Assessment &amp; Plan blocks (cardiology-style)"
        desc="Seed the note with N default A/P blocks for this visit type. The provider can still add or remove blocks while writing the note."
        testId="vt-panel-assessments"
        right={
          <Button size="sm" variant="outline" className="text-[11px] h-7 gap-1" onClick={addSeed} data-testid="vt-assessment-add-btn">
            <Plus className="h-3 w-3" /> Add block
          </Button>
        }
      >
        <div className="space-y-2">
          {(draft.assessments?.seeds || []).length === 0 && (
            <p className="text-[10px] text-slate-500">No seeds — the composer will start with a single empty A/P block.</p>
          )}
          {(draft.assessments?.seeds || []).map((seed, i) => (
            <div key={seed.id} className="flex items-center gap-2" data-testid={`vt-assessment-row-${seed.id}`}>
              <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-500 tabular-nums shrink-0">A/P #{i + 1}</Badge>
              <Input
                value={seed.label}
                onChange={(e) => renameSeed(seed.id, e.target.value)}
                className="h-7 text-[12px] flex-1"
                data-testid={`vt-assessment-label-${seed.id}`}
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => removeSeed(seed.id)}
                data-testid={`vt-assessment-remove-${seed.id}`}
                aria-label={`Remove ${seed.label}`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      </PanelCard>
    </div>
  );
};

export default VisitTypeNoteConfigEditor;
